import React, { useState, useEffect, useRef } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import { liteClient } from 'algoliasearch/lite';
import { listingsAPI, locationsAPI, Listing } from '../api';
import ListingCard from '../components/ListingCard';
import { loadHomeDeliveryLocation } from '../utils/homeLocationStorage';
import { getListingDistances } from '../utils/listingDistanceFilter';
import { algoliaHitToListing, searchAlgoliaListings } from '../services/algoliaSearch';
import { formatListingCategoryLabel, normalizeListingCategory } from '../constants/listingCategories';

interface TopCategory {
  category: string;
  count: number;
}

const searchClient = liteClient('NC39OBLGG3', 'dc77e2a6d622659fd216427832b00f1d');

const PAGE_SIZE = 50;

function countActiveSearchFilters(params: {
  zipCode: string;
  miles: string;
  minPrice: string;
  maxPrice: string;
  condition: string;
  sellerType: string;
}): number {
  return [
    params.zipCode,
    params.miles !== '25' ? params.miles : '',
    params.minPrice,
    params.maxPrice,
    params.condition,
    params.sellerType,
  ].filter(Boolean).length;
}

interface SearchFiltersFieldsProps {
  filterZipCode: string;
  setFilterZipCode: (value: string) => void;
  filterMiles: string;
  setFilterMiles: (value: string) => void;
  filterMinPrice: string;
  setFilterMinPrice: (value: string) => void;
  filterMaxPrice: string;
  setFilterMaxPrice: (value: string) => void;
  filterCondition: string;
  setFilterCondition: (value: string) => void;
  filterSellerType: string;
  setFilterSellerType: (value: string) => void;
  zipLoading: boolean;
  locationLabel: string;
  zipLocation: { lat: number; lng: number } | null;
}

const SearchFiltersFields: React.FC<SearchFiltersFieldsProps> = ({
  filterZipCode,
  setFilterZipCode,
  filterMiles,
  setFilterMiles,
  filterMinPrice,
  setFilterMinPrice,
  filterMaxPrice,
  setFilterMaxPrice,
  filterCondition,
  setFilterCondition,
  filterSellerType,
  setFilterSellerType,
  zipLoading,
  locationLabel,
  zipLocation,
}) => (
  <>
    <div>
      <h3 className="text-sm font-semibold text-gray-800 mb-1">Location &amp; available deliveries</h3>
      <p className="text-xs text-gray-500 mb-3">
        Location is where you receive deliveries. Available deliveries limits results to sellers who can deliver within that distance.
      </p>
      <label className="block text-sm font-medium text-gray-700 mb-2">Location (ZIP)</label>
      <div className="relative mb-4">
        <div className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none">
          <i className="ri-map-pin-line" aria-hidden />
        </div>
        <input
          type="text"
          inputMode="numeric"
          value={filterZipCode}
          onChange={(e) => setFilterZipCode(e.target.value.replace(/\D/g, '').slice(0, 5))}
          placeholder="00000"
          maxLength={5}
          className="w-full max-w-full min-w-0 box-border pl-10 pr-4 py-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-inset focus:ring-green-500 focus:border-green-500 text-base"
        />
      </div>
      {zipLoading && (
        <p className="text-xs text-gray-500 mb-3">Checking delivery area…</p>
      )}
      {!zipLoading && !filterZipCode && locationLabel && zipLocation && (
        <p className="text-xs text-gray-600 mb-3">
          Using delivery location from home: {locationLabel}
        </p>
      )}
      <label className="block text-sm font-medium text-gray-700 mb-2">Available deliveries within (miles)</label>
      <select
        value={filterMiles}
        onChange={(e) => setFilterMiles(e.target.value)}
        className="w-full max-w-full min-w-0 box-border px-4 py-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-inset focus:ring-green-500 focus:border-green-500 text-base"
      >
        <option value="5">5 miles</option>
        <option value="10">10 miles</option>
        <option value="25">25 miles</option>
        <option value="50">50 miles</option>
        <option value="100">100 miles</option>
        <option value="250">250 miles</option>
      </select>
    </div>

    <div>
      <label className="block text-sm font-medium text-gray-700 mb-2">Price range</label>
      <div className="grid grid-cols-2 gap-2">
        <input
          type="number"
          value={filterMinPrice}
          onChange={(e) => setFilterMinPrice(e.target.value)}
          placeholder="Min"
          min="0"
          className="w-full max-w-full min-w-0 box-border px-4 py-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-inset focus:ring-green-500 focus:border-green-500 text-base"
        />
        <input
          type="number"
          value={filterMaxPrice}
          onChange={(e) => setFilterMaxPrice(e.target.value)}
          placeholder="Max"
          min="0"
          className="w-full max-w-full min-w-0 box-border px-4 py-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-inset focus:ring-green-500 focus:border-green-500 text-base"
        />
      </div>
    </div>

    <div>
      <label className="block text-sm font-medium text-gray-700 mb-2">Condition</label>
      <select
        value={filterCondition}
        onChange={(e) => setFilterCondition(e.target.value)}
        className="w-full max-w-full min-w-0 box-border px-4 py-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-inset focus:ring-green-500 focus:border-green-500 text-base"
      >
        <option value="">All</option>
        <option value="new">New</option>
        <option value="new_open_box">New – Open box</option>
        <option value="seller_refurbished">Seller refurbished</option>
        <option value="used">Used</option>
        <option value="for_parts">For parts or not working</option>
      </select>
    </div>

    <div>
      <label className="block text-sm font-medium text-gray-700 mb-2">Seller type</label>
      <select
        value={filterSellerType}
        onChange={(e) => setFilterSellerType(e.target.value)}
        className="w-full max-w-full min-w-0 box-border px-4 py-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-inset focus:ring-green-500 focus:border-green-500 text-base"
      >
        <option value="">All</option>
        <option value="store">Store</option>
        <option value="owner">Owner</option>
      </select>
    </div>
  </>
);

function initialZipFromParams(searchParams: URLSearchParams): string {
  const fromUrl = searchParams.get('zip') || '';
  if (fromUrl) return fromUrl;
  return loadHomeDeliveryLocation()?.zip || '';
}

const Search: React.FC = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [query, setQuery] = useState(searchParams.get('q') || '');
  const [zipCode, setZipCode] = useState(() => initialZipFromParams(searchParams));
  const [miles, setMiles] = useState(searchParams.get('miles') || '25');
  const [minPrice, setMinPrice] = useState(searchParams.get('minPrice') || '');
  const [maxPrice, setMaxPrice] = useState(searchParams.get('maxPrice') || '');
  const [condition, setCondition] = useState(searchParams.get('condition') || '');
  const [sellerType, setSellerType] = useState(searchParams.get('sellerType') || '');
  const [results, setResults] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(false);
  const [searchOffset, setSearchOffset] = useState(0);
  const [zipLocation, setZipLocation] = useState<{ lat: number; lng: number } | null>(null);
  const [zipLoading, setZipLoading] = useState(false);
  const [topCategories, setTopCategories] = useState<TopCategory[]>([]);
  const [searchSuggestions, setSearchSuggestions] = useState<string[]>([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const searchInputRef = useRef<HTMLInputElement>(null);
  const [selectedCategory, setSelectedCategory] = useState(searchParams.get('category') || '');
  
  // Filter state (separate from applied filters)
  const [filterZipCode, setFilterZipCode] = useState(() => initialZipFromParams(searchParams));
  const [filterMiles, setFilterMiles] = useState(searchParams.get('miles') || '25');
  const [filterMinPrice, setFilterMinPrice] = useState(searchParams.get('minPrice') || '');
  const [filterMaxPrice, setFilterMaxPrice] = useState(searchParams.get('maxPrice') || '');
  const [filterCondition, setFilterCondition] = useState(searchParams.get('condition') || '');
  const [filterSellerType, setFilterSellerType] = useState(searchParams.get('sellerType') || '');
  const [locationLabel, setLocationLabel] = useState(() => loadHomeDeliveryLocation()?.label || '');
  const [mobileFiltersOpen, setMobileFiltersOpen] = useState(false);
  const [listingDistances, setListingDistances] = useState<Record<number, number>>({});

  // Load top categories
  useEffect(() => {
    const loadTopCategories = async () => {
      try {
        const categories = await listingsAPI.getTopCategories();
        setTopCategories(categories);
      } catch (error) {
        console.error('Error loading top categories:', error);
      }
    };
    loadTopCategories();
  }, []);

  // Get search suggestions as user types
  useEffect(() => {
    const getSuggestions = async () => {
      if (!query || query.length < 2) {
        setSearchSuggestions([]);
        setShowSuggestions(false);
        return;
      }

      try {
        // Use the same search structure as the main search
        const response = await searchClient.search({
          requests: [
            {
              indexName: 'listings',
              query: query,
              params: {
                hitsPerPage: 5,
              } as any, // Type assertion to bypass TypeScript error
            },
          ],
        });

        const firstResult = response.results?.[0] as any;
        const hits = firstResult?.hits || [];
        
        // Extract unique suggestions from hits (titles and categories)
        const suggestions = new Set<string>();
        hits.forEach((hit: any) => {
          if (hit.title) {
            suggestions.add(hit.title);
          }
          if (hit.category) {
            suggestions.add(hit.category);
          }
        });

        const suggestionsArray = Array.from(suggestions).slice(0, 8);
        
        setSearchSuggestions(suggestionsArray);
        setShowSuggestions(suggestionsArray.length > 0);
      } catch (error) {
        console.error('Error fetching suggestions:', error);
        setSearchSuggestions([]);
        setShowSuggestions(false);
      }
    };

    const timeoutId = setTimeout(() => {
      getSuggestions();
    }, 200);

    return () => clearTimeout(timeoutId);
  }, [query]);

  // Apply filters function
  const applyFilters = () => {
    // Update applied filters from filter state
    setZipCode(filterZipCode);
    setMiles(filterMiles);
    setMinPrice(filterMinPrice);
    setMaxPrice(filterMaxPrice);
    setCondition(filterCondition);
    setSellerType(filterSellerType);
    
    // Update URL params immediately
    const params = new URLSearchParams();
    if (query) params.set('q', query);
    if (selectedCategory) params.set('category', selectedCategory);
    if (filterZipCode) params.set('zip', filterZipCode);
    if (filterMiles) params.set('miles', filterMiles);
    if (filterMinPrice) params.set('minPrice', filterMinPrice);
    if (filterMaxPrice) params.set('maxPrice', filterMaxPrice);
    if (filterCondition) params.set('condition', filterCondition);
    if (filterSellerType) params.set('sellerType', filterSellerType);
    setSearchParams(params, { replace: true });
  };

  // Clear filters function
  const clearFilters = () => {
    setFilterZipCode('');
    setFilterMiles('25');
    setFilterMinPrice('');
    setFilterMaxPrice('');
    setFilterCondition('');
    setFilterSellerType('');
    setZipCode('');
    setMiles('25');
    setMinPrice('');
    setMaxPrice('');
    setCondition('');
    setSellerType('');
    setQuery('');
    setSelectedCategory('');
  };

  // Get lat/lng from zip code or home page delivery location
  useEffect(() => {
    const fetchZipLocation = async () => {
      if (zipCode && zipCode.length === 5) {
        setZipLoading(true);
        try {
          const data = await locationsAPI.lookupZipCode(zipCode);
          if (data && data.latitude && data.longitude) {
            setZipLocation({
              lat: parseFloat(data.latitude),
              lng: parseFloat(data.longitude),
            });
          } else {
            setZipLocation(null);
          }
        } catch (error) {
          console.error('Error fetching zip code location:', error);
          setZipLocation(null);
        } finally {
          setZipLoading(false);
        }
        return;
      }

      const stored = loadHomeDeliveryLocation();
      if (stored?.lat != null && stored?.lng != null) {
        setZipLocation({ lat: stored.lat, lng: stored.lng });
        if (stored.label) setLocationLabel(stored.label);
        return;
      }

      setZipLocation(null);
    };

    fetchZipLocation();
  }, [zipCode]);

  // Perform search
  const performSearch = async (append = false) => {
    if (append) setLoadingMore(true);
    else setLoading(true);
    try {
      const fetchOffset = append ? searchOffset : 0;
      const searchTerm = query.trim();
      let allListings: Listing[];

      if (searchTerm) {
        const page = Math.floor(fetchOffset / PAGE_SIZE);
        const result = await searchAlgoliaListings({ query: searchTerm, page, hitsPerPage: PAGE_SIZE });
        const categoryKey = selectedCategory ? normalizeListingCategory(selectedCategory) : '';
        allListings = result.hits
          .filter((hit) => !categoryKey || normalizeListingCategory(hit.category || '') === categoryKey)
          .map((hit) => ({ ...algoliaHitToListing(hit), sold: 0 }));
        setSearchOffset(fetchOffset + PAGE_SIZE);
        setHasMore(page + 1 < result.nbPages);
      } else {
        const filters: any = { limit: PAGE_SIZE, offset: fetchOffset };
        if (selectedCategory) filters.category = selectedCategory;
        allListings = await listingsAPI.getAll(filters);
        const rawCount = Array.isArray(allListings) ? allListings.length : 0;
        setSearchOffset(fetchOffset + rawCount);
        setHasMore(rawCount >= PAGE_SIZE);
      }

      // Filter listings client-side
        let filteredListings = allListings.filter(
          (listing: Listing) => listing.sold === 0 || listing.sold === false
        );

        // Apply condition filter
        if (condition) {
          filteredListings = filteredListings.filter(
            (listing: Listing) => {
              const listingCondition = listing.condition || 'new';
              return listingCondition === condition;
            }
          );
        }

        // Apply seller type filter
        if (sellerType === 'store') {
          filteredListings = filteredListings.filter(
            (listing: Listing) => listing.store_id || listing.store_name
          );
        } else if (sellerType === 'owner') {
          filteredListings = filteredListings.filter(
            (listing: Listing) => !listing.store_id && !listing.store_name
          );
        }

        // Apply price filters
        if (minPrice && minPrice.trim() !== '') {
          const minPriceNum = parseFloat(minPrice);
          if (!isNaN(minPriceNum)) {
            filteredListings = filteredListings.filter(
              (listing: Listing) => (listing.price || 0) >= minPriceNum
            );
          }
        }
        if (maxPrice && maxPrice.trim() !== '') {
          const maxPriceNum = parseFloat(maxPrice);
          if (!isNaN(maxPriceNum)) {
            filteredListings = filteredListings.filter(
              (listing: Listing) => (listing.price || 0) <= maxPriceNum
            );
          }
        }

        // Distance from delivery location + optional radius filter
        let pageDistances: Record<number, number> = {};
        if (zipLocation) {
          pageDistances = await getListingDistances(filteredListings, zipLocation);
          const milesNum = parseFloat(miles);
          if (!isNaN(milesNum) && milesNum > 0) {
            filteredListings = filteredListings.filter((listing: Listing) => {
              const distance = pageDistances[listing.id];
              return distance != null && distance <= milesNum;
            });
          }
          if (append) {
            setListingDistances((prev) => ({ ...prev, ...pageDistances }));
          } else {
            setListingDistances(pageDistances);
          }
        } else if (!append) {
          setListingDistances({});
        }

      if (append) {
        setResults(prev => [...prev, ...filteredListings]);
      } else {
        setResults(filteredListings);
      }
    } catch (error) {
      console.error('Search error:', error);
      if (!append) setResults([]);
      if (!append) setListingDistances({});
    } finally {
      setLoading(false);
      if (append) setLoadingMore(false);
    }
  };

  // Run search when filters change
  useEffect(() => {
    setSearchOffset(0);
    setHasMore(false);
    const hasFilters = query || selectedCategory || zipCode || minPrice || maxPrice || condition || sellerType;
    
    if (!hasFilters) {
      performSearch();
    } else {
      const timeoutId = setTimeout(() => performSearch(), 300);
      return () => clearTimeout(timeoutId);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [query, selectedCategory, zipLocation, miles, minPrice, maxPrice, condition, sellerType, zipCode]);

  // Sync filter state with applied filters when URL params change (on mount or when URL changes externally)
  useEffect(() => {
    const urlZip = searchParams.get('zip') || '';
    const urlMiles = searchParams.get('miles') || '25';
    const urlMinPrice = searchParams.get('minPrice') || '';
    const urlMaxPrice = searchParams.get('maxPrice') || '';
    const urlCondition = searchParams.get('condition') || '';
    const urlSellerType = searchParams.get('sellerType') || '';
    
    // Sync filter state with URL params (only if different to avoid unnecessary updates)
    if (urlZip !== filterZipCode) setFilterZipCode(urlZip);
    if (urlMiles !== filterMiles) setFilterMiles(urlMiles);
    if (urlMinPrice !== filterMinPrice) setFilterMinPrice(urlMinPrice);
    if (urlMaxPrice !== filterMaxPrice) setFilterMaxPrice(urlMaxPrice);
    if (urlCondition !== filterCondition) setFilterCondition(urlCondition);
    if (urlSellerType !== filterSellerType) setFilterSellerType(urlSellerType);
    
    // Sync applied filters with URL params (only if different to avoid unnecessary updates)
    if (urlZip !== zipCode) setZipCode(urlZip);
    if (urlMiles !== miles) setMiles(urlMiles);
    if (urlMinPrice !== minPrice) setMinPrice(urlMinPrice);
    if (urlMaxPrice !== maxPrice) setMaxPrice(urlMaxPrice);
    if (urlCondition !== condition) setCondition(urlCondition);
    if (urlSellerType !== sellerType) setSellerType(urlSellerType);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams.toString()]);

  // Update URL params when filters change
  useEffect(() => {
    const params = new URLSearchParams();
    if (query) params.set('q', query);
    if (selectedCategory) params.set('category', selectedCategory);
    if (zipCode) params.set('zip', zipCode);
    if (miles) params.set('miles', miles);
    if (minPrice) params.set('minPrice', minPrice);
    if (maxPrice) params.set('maxPrice', maxPrice);
    if (condition) params.set('condition', condition);
    if (sellerType) params.set('sellerType', sellerType);
    setSearchParams(params, { replace: true });
  }, [query, selectedCategory, zipCode, miles, minPrice, maxPrice, condition, sellerType, setSearchParams]);

  const activeFilterCount = countActiveSearchFilters({
    zipCode,
    miles,
    minPrice,
    maxPrice,
    condition,
    sellerType,
  });

  const handleApplyFilters = () => {
    applyFilters();
    setMobileFiltersOpen(false);
  };

  const handleClearFilters = () => {
    clearFilters();
    setMobileFiltersOpen(false);
  };

  const filterFieldProps = {
    filterZipCode,
    setFilterZipCode,
    filterMiles,
    setFilterMiles,
    filterMinPrice,
    setFilterMinPrice,
    filterMaxPrice,
    setFilterMaxPrice,
    filterCondition,
    setFilterCondition,
    filterSellerType,
    setFilterSellerType,
    zipLoading,
    locationLabel,
    zipLocation,
  };

  const hasAnyFilters =
    query ||
    zipCode ||
    minPrice ||
    maxPrice ||
    condition ||
    sellerType ||
    filterZipCode ||
    filterMinPrice ||
    filterMaxPrice ||
    filterCondition ||
    filterSellerType;

  /** Prefer category pill total when search isn't narrowed further (Found was only the loaded page size). */
  const foundCount = (() => {
    const milesNum = parseFloat(miles);
    const distanceActive = !!zipLocation && Number.isFinite(milesNum) && milesNum > 0;
    const narrowed =
      distanceActive ||
      !!(query || minPrice || maxPrice || condition || sellerType);
    if (selectedCategory && !narrowed) {
      const key = normalizeListingCategory(selectedCategory);
      const cat = topCategories.find(
        (c) => normalizeListingCategory(String(c.category)) === key
      );
      const total = cat != null ? Number(cat.count) : NaN;
      if (Number.isFinite(total) && total >= results.length) return total;
    }
    return results.length;
  })();

  return (
    <div className="min-h-screen bg-gray-50 py-5 sm:py-8 overflow-x-hidden">
      <div className="mx-auto w-full max-w-7xl min-w-0 px-2 sm:px-3 md:px-4">
        <h1 className="text-2xl sm:text-3xl font-bold text-gray-800 mb-4 sm:mb-6">Search Listings</h1>

        {/* Top Categories */}
        {topCategories.length > 0 && (
          <div className="bg-white rounded-lg shadow-md p-3 sm:p-4 mb-4 sm:mb-6 max-w-full overflow-hidden">
            <h2 className="text-base sm:text-lg font-semibold text-gray-800 mb-3 sm:mb-4">Top Categories</h2>
            <div className="w-full max-w-full overflow-x-auto overflow-y-hidden py-5 sm:py-0 [scrollbar-width:none] [-ms-overflow-style:none] [&::-webkit-scrollbar]:hidden">
              <div className="flex w-max max-w-none flex-nowrap gap-2 pr-1">
                {topCategories.map((cat) => (
                  <button
                    key={cat.category}
                    type="button"
                    onClick={() => {
                      const next =
                        selectedCategory === cat.category ? '' : cat.category;
                      setSelectedCategory(next);
                      setQuery('');
                    }}
                    className={`shrink-0 whitespace-nowrap px-3 py-1.5 sm:px-4 sm:py-2 rounded-full text-xs sm:text-sm font-medium transition ${
                      selectedCategory === cat.category
                        ? 'bg-green-500 text-white'
                        : 'bg-gray-100 hover:bg-green-100 text-gray-700 hover:text-green-800'
                    }`}
                  >
                    {formatListingCategoryLabel(String(cat.category))} ({Number(cat.count) || 0})
                  </button>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* Search Bar — desktop */}
        <div className="hidden lg:block bg-white rounded-lg shadow-md p-3 sm:p-4 mb-4 sm:mb-6 overflow-hidden">
          <div className="relative min-w-0">
            <div className="absolute left-3 sm:left-4 top-1/2 transform -translate-y-1/2 text-gray-400 pointer-events-none">
              <i className="ri-search-line text-lg sm:text-xl"></i>
            </div>
            <input
              ref={searchInputRef}
              type="text"
              value={query}
              onChange={(e) => {
                setQuery(e.target.value);
              }}
              onFocus={() => {
                if (query.length >= 2 && searchSuggestions.length > 0) {
                  setShowSuggestions(true);
                }
              }}
              onBlur={() => {
                setTimeout(() => setShowSuggestions(false), 200);
              }}
              placeholder="Search listings..."
              className="w-full min-w-0 bg-white pl-10 sm:pl-12 pr-4 py-2.5 sm:py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-0 text-base sm:text-lg"
            />
            {showSuggestions && searchSuggestions.length > 0 && (
              <div className="absolute z-50 w-full mt-1 bg-white border border-gray-300 rounded-lg shadow-lg max-h-60 overflow-y-auto" style={{ top: '100%' }}>
                {searchSuggestions.map((suggestion, index) => (
                  <button
                    key={index}
                    type="button"
                    onMouseDown={(e) => {
                      e.preventDefault();
                      setQuery(suggestion);
                      setShowSuggestions(false);
                    }}
                    className="w-full text-left px-4 py-2 hover:bg-gray-100 focus:bg-gray-100 focus:outline-none transition-colors cursor-pointer"
                  >
                    <span className="text-gray-700">{suggestion}</span>
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Search + filters — mobile */}
        <div className="lg:hidden bg-white rounded-lg shadow-md border border-gray-200 p-4 mb-4 overflow-hidden">
          <div className="flex items-center gap-2 min-w-0 max-w-full">
            <div className="relative flex-1 basis-0 min-w-0">
              <div className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none">
                <i className="ri-search-line text-lg" aria-hidden />
              </div>
              <input
                ref={searchInputRef}
                type="search"
                enterKeyHint="search"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                onFocus={() => {
                  if (query.length >= 2 && searchSuggestions.length > 0) {
                    setShowSuggestions(true);
                  }
                }}
                onBlur={() => {
                  setTimeout(() => setShowSuggestions(false), 200);
                }}
                placeholder="Search listings..."
                className="w-full max-w-full min-w-0 box-border bg-white pl-10 pr-4 py-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-0 text-base"
              />
              {showSuggestions && searchSuggestions.length > 0 && (
                <div className="absolute z-50 left-0 right-0 mt-1 bg-white border border-gray-300 rounded-lg shadow-lg max-h-60 overflow-y-auto" style={{ top: '100%' }}>
                  {searchSuggestions.map((suggestion, index) => (
                    <button
                      key={index}
                      type="button"
                      onMouseDown={(e) => {
                        e.preventDefault();
                        setQuery(suggestion);
                        setShowSuggestions(false);
                      }}
                      className="w-full text-left px-4 py-2 hover:bg-gray-100 focus:bg-gray-100 focus:outline-none transition-colors cursor-pointer"
                    >
                      <span className="text-gray-700">{suggestion}</span>
                    </button>
                  ))}
                </div>
              )}
            </div>
            <button
              type="button"
              onClick={() => setMobileFiltersOpen((open) => !open)}
              aria-expanded={mobileFiltersOpen}
              aria-label="Filters"
              className={`relative shrink-0 flex items-center justify-center w-11 h-11 rounded-lg border transition-all ${
                mobileFiltersOpen || activeFilterCount > 0
                  ? 'bg-green-600 border-green-600 text-white'
                  : 'bg-white border-gray-300 text-gray-700 hover:bg-gray-50'
              }`}
            >
              <i className="ri-equalizer-line text-xl" aria-hidden />
              {activeFilterCount > 0 && (
                <span className="absolute top-0 right-0 translate-x-1/4 -translate-y-1/4 min-w-[16px] h-4 px-1 flex items-center justify-center text-[10px] font-bold text-green-700 bg-white border border-green-600 rounded-full pointer-events-none">
                  {activeFilterCount}
                </span>
              )}
            </button>
          </div>

          {mobileFiltersOpen && (
            <div className="flex flex-col gap-5 mt-4 pt-4 border-t border-gray-200">
              <SearchFiltersFields {...filterFieldProps} />
              <div className="flex flex-col gap-2">
                <button
                  type="button"
                  onClick={handleApplyFilters}
                  className="w-full px-4 py-2.5 bg-green-500 hover:bg-green-600 text-white rounded-lg font-medium transition-colors"
                >
                  Apply filters
                </button>
                {hasAnyFilters && (
                  <button
                    type="button"
                    onClick={handleClearFilters}
                    className="w-full px-4 py-2.5 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-lg font-medium transition-colors"
                  >
                    Clear filters
                  </button>
                )}
              </div>
            </div>
          )}
        </div>

        {/* Main Content with Sidebar */}
        <div className="grid grid-cols-1 lg:grid-cols-4 gap-3 sm:gap-4 lg:gap-6 min-w-0">
          {/* Sidebar Filters - hidden on mobile */}
          <div className="hidden lg:block lg:col-span-1">
            <div className="bg-white rounded-lg shadow-md p-6 sticky top-4">
              <h2 className="text-lg font-semibold text-gray-800">Filters</h2>
              <p className="text-xs text-gray-500 mt-1 mb-4">
                Set your <strong className="text-gray-700">location</strong> and{' '}
                <strong className="text-gray-700">available deliveries within</strong> miles.
              </p>

              <div className="space-y-6 mb-6">
                <SearchFiltersFields {...filterFieldProps} />
              </div>

              <div className="space-y-3">
                <button
                  type="button"
                  onClick={applyFilters}
                  className="w-full px-4 py-2 bg-green-500 hover:bg-green-600 text-white rounded-lg font-medium transition-colors"
                >
                  Apply Filters
                </button>
                {hasAnyFilters && (
                  <button
                    type="button"
                    onClick={clearFilters}
                    className="w-full px-4 py-2 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-lg font-medium transition-colors"
                  >
                    Clear Filters
                  </button>
                )}
              </div>
            </div>
          </div>

          {/* Search Results */}
          <div className="lg:col-span-3 min-w-0">

            {/* Results */}
            {loading ? (
              <div className="text-center py-12">
                <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-green-600"></div>
                <p className="mt-4 text-gray-600">Searching...</p>
              </div>
            ) : results.length > 0 ? (
              <>
                <div className="mb-4">
                  <p className="text-gray-600">
                    Found{' '}
                    <strong>
                      {foundCount}
                      {hasMore && foundCount === results.length ? '+' : ''}
                    </strong>{' '}
                    result{foundCount !== 1 ? 's' : ''}
                  </p>
                </div>
                <div className="grid grid-cols-2 lg:grid-cols-2 xl:grid-cols-3 gap-1.5 sm:gap-3 lg:gap-4 min-w-0">
                  {results.map((listing) => (
                    <ListingCard
                      key={listing.id}
                      listing={listing}
                      showBadges={false}
                      showOpenStatus={false}
                      showDistanceFooter
                      distanceMiles={listingDistances[listing.id]}
                    />
                  ))}
                </div>
                {hasMore && (
                  <div className="mt-8 flex justify-center">
                    <button
                      onClick={() => performSearch(true)}
                      disabled={loadingMore}
                      className="px-6 py-3 bg-green-600 hover:bg-green-700 disabled:opacity-70 text-white font-medium rounded-lg transition flex items-center gap-2"
                    >
                      {loadingMore ? (
                        <>
                          <div className="animate-spin rounded-full h-5 w-5 border-2 border-white border-t-transparent" />
                          Loading...
                        </>
                      ) : (
                        'Load more'
                      )}
                    </button>
                  </div>
                )}
              </>
            ) : (
              <div className="text-center py-12 bg-white rounded-lg shadow-md">
                <p className="text-gray-600 text-lg">No listings found</p>
                <p className="text-gray-500 mt-2">Try adjusting your search filters</p>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Search;

